/**
 * The message that sends a change order to the client (2026-09-30).
 *
 * Until now "Send" queued an SMS to EVERY project contact with a phone —
 * clients and subcontractors alike — through the notifications table, whose
 * dispatcher was parked in favor of the native share sheet in Phase 19. So a
 * change order was, in practice, sent to nobody.
 *
 * It now goes out the way a contractor reminder and a punch list do: composed
 * here, handed to the device's share sheet, and recorded afterwards. A change
 * order is between the designer and the CLIENT — a subcontractor has no
 * business seeing the design fee — so the recipient is the client, and only
 * the client.
 *
 * Pure, and composed at RENDER time: navigator.share() must be called with the
 * text already in hand (see share-sheet.ts's gesture trap).
 */

import { fmtMoney } from '@/lib/dom';
import { changeOrderTotal } from '@/lib/change-order-budget';
import type { ChangeOrderRow, DesignerContactRow, ProjectRow } from '@/types/database';

export type MessageFields = Pick<ChangeOrderRow,
  'number' | 'title' | 'cost_delta_cents' | 'designer_fee_cents'>;

export interface ChangeOrderMessageInput {
  co: MessageFields;
  projectName: string;
  /** The client, for the greeting. */
  clientName?: string | null;
  /** The firm, so the client knows who it is from. Never a built-in name. */
  fromName?: string | null;
}

/**
 * "Hi Sarah — Change Order #3 for Westlake: Upgrade tile. It adds $5,500 to
 *  the budget. You can review and approve it here: — Searle Studio"
 *
 * The URL is NOT in this string: shareOrFallback appends it (composeShareText),
 * and a URL glued on here would be sent twice.
 */
export function changeOrderMessage(input: ChangeOrderMessageInput): string {
  const { co, projectName } = input;
  const first = firstName(input.clientName);
  const hello = first ? `Hi ${first} — ` : '';
  const from = (input.fromName ?? '').trim();
  const signoff = from ? `\n— ${from}` : '';
  return `${hello}Change Order #${co.number} for ${projectName}: ${co.title}. `
    + `${moneySentence(changeOrderTotal(co))} `
    + `You can review and approve it here:${signoff}`;
}

/**
 * "It adds $5,500 to the budget." / "It reduces the budget by $1,200." /
 * "It doesn't change the budget."
 *
 * A change order raises the BUDGET, not the projected cost (0038) — so the
 * sentence says budget, and a credit is phrased as a reduction rather than as
 * "adds −$1,200", which reads as a mistake.
 */
export function moneySentence(totalCents: number): string {
  if (!totalCents) return 'It doesn’t change the budget.';
  if (totalCents < 0) return `It reduces the budget by ${fmtMoney(Math.abs(totalCents))}.`;
  return `It adds ${fmtMoney(totalCents)} to the budget.`;
}

/** The share sheet's title — what the OS shows above the app list. */
export function changeOrderShareTitle(co: Pick<MessageFields, 'number'>, projectName: string): string {
  return `${projectName} — Change Order #${co.number}`;
}

function firstName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  // "The Andersons" is a household, not a person: greeting it "Hi The" is
  // worse than not greeting it at all. Mick's own client is recorded that way.
  const first = trimmed.split(/\s+/)[0] ?? '';
  return /^(the|mr|mrs|ms|dr|mr\.|mrs\.|ms\.|dr\.)$/i.test(first) ? trimmed : first;
}

/** The fields picking a recipient reads. */
export type RecipientFields = Pick<DesignerContactRow, 'id' | 'name' | 'phone' | 'is_client'>;

export interface ChangeOrderRecipient {
  contactId: string | null;
  name: string | null;
  phone: string | null;
}

/**
 * Who a change order goes to: the project's client.
 *
 * Deliberately NOT everyone with a phone — that is what this replaced, and it
 * put the design fee in front of every subcontractor on the job. A client with
 * a phone wins over one without, so a household recorded twice (an email
 * contact and a texting one) still gets a number for the sms: fallback.
 *
 * With no client contact at all, the share sheet asks who to send to and the
 * greeting falls back to the project's own `client_name` — which is where
 * Mick's clients are actually recorded today. Returning null here rather than
 * guessing at a contact is the point: the message still sends.
 */
export function changeOrderRecipient(
  contacts: readonly RecipientFields[],
  project: Pick<ProjectRow, 'client_name'>,
): ChangeOrderRecipient {
  const clients = contacts.filter((c) => c.is_client);
  const chosen = clients.find((c) => (c.phone ?? '').trim()) ?? clients[0] ?? null;
  return {
    contactId: chosen?.id ?? null,
    // The contact's name when there is one, else whatever the project calls
    // the client. Never a built-in.
    name: (chosen?.name ?? '').trim() || (project.client_name ?? '').trim() || null,
    phone: (chosen?.phone ?? '').trim() || null,
  };
}
